import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Nav, Row, Spinner, Table } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { ORDER_TYPES, UPLOAD_BASE, beYearOptions, currentBeYear, fmtDateTH } from './aoShared'

const emptyForm = { od_type: 'order', od_title: '', od_date: '', od_detail: '' }

export default function OrdersRegistry() {
  const navigate = useNavigate()
  const [tab, setTab] = useState('order')
  const [year, setYear] = useState(String(currentBeYear()))
  const [q, setQ] = useState('')
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [file, setFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  // ผู้เกี่ยวข้อง
  const [teachers, setTeachers] = useState([])
  const [selected, setSelected] = useState(new Map()) // t_id -> role
  const [filter, setFilter] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ type: tab })
      if (year) params.set('year', year)
      if (q) params.set('q', q)
      const res = await axiosInstance.get(`/api/ao/orders?${params.toString()}`)
      if (res.data.success) setOrders(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [tab, year, q])

  useEffect(() => { fetchData() }, [fetchData])

  const openModal = async () => {
    setForm({ ...emptyForm, od_type: tab, od_date: new Date().toLocaleDateString('en-CA') })
    setFile(null)
    setSelected(new Map())
    setFilter('')
    setModalError('')
    setShowModal(true)
    if (!teachers.length) {
      try {
        const res = await axiosInstance.get('/api/ao/teachers')
        if (res.data.success) setTeachers(res.data.data)
      } catch {
        setModalError('โหลดรายชื่อบุคลากรไม่สำเร็จ')
      }
    }
  }

  const visibleTeachers = useMemo(() => {
    const f = filter.trim().toLowerCase()
    return teachers.filter(t =>
      !f || t.t_name.toLowerCase().includes(f) || (t.hp_department || '').toLowerCase().includes(f))
  }, [teachers, filter])

  const toggle = (tid) => setSelected(prev => {
    const next = new Map(prev)
    if (next.has(tid)) next.delete(tid)
    else next.set(tid, '')
    return next
  })

  const setRole = (tid, role) => setSelected(prev => {
    const next = new Map(prev)
    next.set(tid, role)
    return next
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setModalError('')
    try {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''))
      fd.append('assignees', JSON.stringify([...selected].map(([t_id, role]) => ({ t_id, role }))))
      if (file) fd.append('file', file)
      const res = await axiosInstance.post('/api/ao/orders', fd)
      if (res.data.success) {
        setShowModal(false)
        Swal.fire({ icon: 'success', title: `ออกเลข ${res.data.data.od_full_no} เรียบร้อย`, timer: 2000, showConfirmButton: false })
        fetchData()
      }
    } catch (err) {
      setModalError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const tt = ORDER_TYPES[form.od_type] || ORDER_TYPES.order

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-megaphone-line me-2 text-primary" />
              ทะเบียนคำสั่ง / ประกาศโรงเรียน
            </h4>
            <Button variant="primary" size="sm" onClick={openModal}>
              <i className="ri-add-line me-1" />ออก{ORDER_TYPES[tab].label}
            </Button>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
              <Nav variant="pills" className="fs-sm" activeKey={tab} onSelect={k => setTab(k)}>
                {Object.entries(ORDER_TYPES).map(([k, v]) => (
                  <Nav.Item key={k}><Nav.Link eventKey={k}>{v.label}</Nav.Link></Nav.Item>
                ))}
              </Nav>
              <div className="ms-auto d-flex gap-2">
                <Form.Select size="sm" style={{ width: 120 }} value={year} onChange={e => setYear(e.target.value)}>
                  {beYearOptions().map(y => <option key={y} value={y}>พ.ศ. {y}</option>)}
                </Form.Select>
                <Form.Control size="sm" style={{ width: 200 }} placeholder="ค้นหา เลขที่/ชื่อเรื่อง"
                  value={q} onChange={e => setQ(e.target.value)} />
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : orders.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่พบ{ORDER_TYPES[tab].label}ในเงื่อนไขที่เลือก</div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 100 }}>เลขที่</th>
                      <th style={{ width: 120 }}>วันที่</th>
                      <th>ชื่อเรื่อง</th>
                      <th className="text-center" style={{ width: 110 }}>รับทราบ</th>
                      <th style={{ width: 60 }}>ไฟล์</th>
                      <th style={{ width: 90 }}>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(o => (
                      <tr key={o.od_id} style={{ cursor: 'pointer', opacity: o.od_status === 'cancelled' ? 0.55 : 1 }}
                        onClick={() => navigate(`/admin-office/orders/${o.od_id}`)}>
                        <td className="fw-semibold">{o.od_full_no}</td>
                        <td>{fmtDateTH(o.od_date)}</td>
                        <td className="text-truncate" style={{ maxWidth: 320 }} title={o.od_title}>
                          <Link to={`/admin-office/orders/${o.od_id}`} onClick={e => e.stopPropagation()}>{o.od_title}</Link>
                        </td>
                        <td className="text-center">{o.assignee_count > 0 ? `${o.ack_count}/${o.assignee_count}` : '-'}</td>
                        <td onClick={e => e.stopPropagation()}>
                          {o.od_file ? (
                            <a href={`${UPLOAD_BASE}/uploads/ao_orders/${o.od_file}`} target="_blank" rel="noreferrer" title="เปิดไฟล์">
                              <i className="ri-attachment-2" />
                            </a>
                          ) : '-'}
                        </td>
                        <td>
                          {o.od_status === 'cancelled'
                            ? <Badge bg="secondary">ยกเลิก</Badge>
                            : <Badge bg="success">ใช้งาน</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />

      {/* Create modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">ออก{tt.label} (ออกเลขอัตโนมัติ)</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Row className="g-2 mb-2">
              <Col xs={5}>
                <Form.Label className="fs-sm mb-1">ประเภท</Form.Label>
                <Form.Select size="sm" value={form.od_type}
                  onChange={e => setForm(f => ({ ...f, od_type: e.target.value }))}>
                  {Object.entries(ORDER_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">วันที่ <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="date" required value={form.od_date}
                  onChange={e => setForm(f => ({ ...f, od_date: e.target.value }))} />
              </Col>
              <Col xs={3}>
                <Form.Label className="fs-sm mb-1">ไฟล์ (PDF ลงนาม)</Form.Label>
                <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={e => setFile(e.target.files[0] || null)} />
              </Col>
            </Row>
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">ชื่อเรื่อง <span className="text-danger">*</span></Form.Label>
              <Form.Control size="sm" type="text" maxLength={500} required value={form.od_title}
                placeholder={`เช่น ${form.od_type === 'appointment' ? 'แต่งตั้งคณะกรรมการดำเนินงานกีฬาสี ประจำปี...' : 'เรื่อง ...'}`}
                onChange={e => setForm(f => ({ ...f, od_title: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">รายละเอียด <span className="text-muted">— ไม่บังคับ</span></Form.Label>
              <Form.Control size="sm" as="textarea" rows={2} value={form.od_detail}
                onChange={e => setForm(f => ({ ...f, od_detail: e.target.value }))} />
            </Form.Group>

            <Form.Label className="fs-sm mb-1 d-flex align-items-center">
              ผู้เกี่ยวข้อง / ผู้ได้รับมอบหมาย
              <span className="ms-auto text-muted fw-normal">เลือกแล้ว {selected.size} คน</span>
            </Form.Label>
            <Form.Control size="sm" className="mb-2" placeholder="ค้นหาชื่อ/ฝ่าย"
              value={filter} onChange={e => setFilter(e.target.value)} />
            <div className="border rounded" style={{ maxHeight: 240, overflowY: 'auto' }}>
              {visibleTeachers.map(t => (
                <div key={t.t_id} className="d-flex align-items-center px-3 py-1 border-bottom gap-2">
                  <Form.Check type="checkbox" id={`ord-t-${t.t_id}`} className="flex-grow-1"
                    label={`${t.t_name}${t.hp_department ? ` · ${t.hp_department}` : ''}`}
                    checked={selected.has(t.t_id)}
                    onChange={() => toggle(t.t_id)} />
                  {selected.has(t.t_id) && form.od_type === 'appointment' && (
                    <Form.Control size="sm" style={{ width: 160 }} placeholder="บทบาท เช่น ประธาน"
                      value={selected.get(t.t_id)}
                      onChange={e => setRole(t.t_id, e.target.value)} />
                  )}
                </div>
              ))}
              {!visibleTeachers.length && <div className="text-center text-muted py-3 fs-sm">ไม่พบบุคลากร</div>}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-megaphone-line me-1" />}
              ออกเอกสาร
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
